import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { WorkoutTypeRuleUpdateRequestDto } from '../../core/api/FitnessActivityClient';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';
import { links } from '../../core/router';
import {
    toWorkoutValidationRuleFormData,
    toWorkoutValidationRulePayload,
    WorkoutValidationRuleForm,
    WorkoutValidationRuleFormData
} from './workout-validation-rule-form';

export const EditWorkoutTypeRule: React.FC = () => {
    const { ruleId } = useParams();
    const navigate = useNavigate();
    const { fitnessActivityClient } = injectTSDI(Api);

    const ruleDetails = useWrapRequest(async (id: number) => {
        const result =
            await fitnessActivityClient.admin.workoutTypeRuleControllerGetDetails(
                id
            );
        return result.data;
    });

    React.useEffect(() => {
        if (ruleId) {
            ruleDetails.request([parseInt(ruleId, 10)]);
        }
    }, [ruleId]);

    const handleSubmit = async (formData: WorkoutValidationRuleFormData) => {
        if (!ruleId) {
            throw new Error('Rule ID is missing');
        }

        const requestData: WorkoutTypeRuleUpdateRequestDto =
            toWorkoutValidationRulePayload(formData);

        await fitnessActivityClient.admin.workoutTypeRuleControllerUpdate(
            parseInt(ruleId, 10),
            requestData
        );

        navigate(links.workoutTypeRules());
    };

    if (ruleDetails.loading || (!ruleDetails.result && !ruleDetails.error)) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (ruleDetails.error || !ruleDetails.result) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {ruleDetails.error?.message ||
                        'Failed to load workout validation rule'}
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <WorkoutValidationRuleForm
            title="Edit Workout Validation Rule"
            submitLabel="Save"
            submittingLabel="Saving..."
            initialData={toWorkoutValidationRuleFormData(ruleDetails.result)}
            onCancel={() => navigate(links.workoutTypeRules())}
            onSubmit={handleSubmit}
        />
    );
};
